<template>
  <main class="fiat-payout-history-wrapper">
    <h1 class="page-title">Fiat Payouts admin</h1>

    <fiat-balances title="Available Transfer Balance" />

    <form v-on:reset="handleReset">
      <div>
        <fieldset>
          <label>Fiat Payout reference
            <input type="text" v-model="searchForm.payout_ref" /></label>
          <label>Sender name <input type="text" v-model="searchForm.sender_name" /></label>
        </fieldset>
        <fieldset>
          <legend>Last status update</legend>
          <label>From<input
              type="date"
              v-model="searchForm.from_status_date"
              :class="{}" /></label>
          <label>To<input
              type="date"
              v-model="searchForm.to_status_date"
              :class="{}" /></label>
          <label>Status
            <select v-model="searchForm.status">
              <option value=""></option>
              <option value="created">Created</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="submitted">Submitted</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
              <option value="declined">Declined</option>
              <option value="reversed">Reversed</option>
              <option value="available">Available</option>
            </select>
          </label>
        </fieldset>
      </div>
      <div v-if="shouldAdvancedSearchDisplay">
        <fieldset>
          <legend>Currency</legend>
          <label>Sending currency
            <input
              v-model="searchForm.sending_currency"
              list="sendingCurrencies" />
            <datalist id="sendingCurrencies">
              <option
                v-for="currency in supportedOverallCurrenciesGetter"
                :key="currency"
                :value="currency">
                {{ currency }}
              </option>
            </datalist>
          </label>
          <label>Receiving currency
            <input
              v-model="searchForm.receiving_currency"
              list="receivingCurrencies" />
            <datalist id="receivingCurrencies">
              <option
                v-for="currency in supportedFiatCurrenciesGetter"
                :key="currency"
                :value="currency">
                {{ currency }}
              </option>
            </datalist>
          </label>
        </fieldset>
        <fieldset v-if="shouldAdvancedSearchDisplay">
          <legend>Creation Date</legend>
          <label>From<input
              type="date"
              v-model="searchForm.from_creation_date"
              :class="{}" /></label>
          <label>To<input
              type="date"
              v-model="searchForm.to_creation_date"
              :class="{}" /></label>
        </fieldset>
      </div>
      <ul class="button-grp">
        <li><button @click.prevent="findPayouts()" class="btn icon-btn search">Search</button></li>
        <li>
          <button @click.prevent="toggleFiatAdvancedSearch()" class="btn icon-btn" :class="{ 'expand': !shouldAdvancedSearchDisplay, 'less': shouldAdvancedSearchDisplay }">
            {{ shouldAdvancedSearchDisplay ? 'Less parameters' : 'More parameters' }}
          </button>
        </li>
        <li><button class="btn icon-btn reset" @click.prevent="handleResetform()">Clear form</button> </li>
        <li>
          <router-link class="btn outline" to="/create-fiat-payout">
            Create new payout
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#075593">
              <path fill="none" d="M0 0h24v24H0z" />
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
          </router-link>
        </li>
        <li class="download">
          <button
            @click.prevent="downloadFiatPayoutsCSV()"
            ref="downloadFiatPayoutsCSVBtn"
            class="btn icon-btn download">
            Download as CSV
          </button>
        </li>
      </ul>

      <ul class="errors-section" v-if="errorDownload || errorFind">
        <li v-if="errorDownload">
          Error in downloading csv file, please try again.
        </li>
        <li v-if="errorFind">
          Error in finding fiat payouts, please try again.
        </li>
      </ul>
    </form>

    <div class="table-wrapper-custom" ref="fiatPayoutHistoryTable">
      <div>
        <table>
          <thead>
            <tr>
              <th class="sticky-col left">Fiat Payout Reference</th>
              <th>Status</th>
              <th>Sender Name</th>
              <th>Sending Amount</th>
              <th>Recipient Name</th>
              <th>Received Amount</th>
              <th>Created Date</th>
              <th>Last Modified</th>
            </tr>
          </thead>
          <tbody v-if="fiatPayoutListGetter.length > 0">
            <tr :key="payout.id" v-for="payout in fiatPayoutListGetter">
              <td class="sticky-col left">
                <button class="btn link-style" @click="openDetailsModal(payout)">
                  {{ payout.reference_number }}
                </button>
              </td>
              <td class="status">
                <Tag :class="payout.status">{{ payout.status }}</Tag>
              </td>
              <td>
                {{ payout.sender_info ? payout.sender_info.sender_name : '' }}
              </td>
              <td>
                {{ payout.sending_currency }}
                {{ formatFiatPrice(payout.sending_amount) }}
              </td>
              <td>
                {{
                payout.destination_account
                ? payout.destination_account.account.account_name
                : ''
                }}
              </td>
              <td>
                {{ payout.receiving_currency }}
                {{ formatFiatPrice(payout.receiving_amount) }}
              </td>
              <td>
                {{
                payout.created_at
                ? `${formatToLocalDate(
                payout.created_at
                )}, ${formatToLocalTime(payout.created_at)}`
                : '-'
                }}
              </td>
              <td>
                {{
                payout.updated_at
                ? `${formatToLocalDate(
                payout.updated_at
                )}, ${formatToLocalTime(payout.updated_at)}`
                : '-'
                }}
              </td>
            </tr>
          </tbody>
        </table>
        <p
          class="emptyTable"
          v-if="!fiatPayoutListGetter ||
            (fiatPayoutListGetter && fiatPayoutListGetter.length <= 0)
          ">
          {{
          searchMade
          ? 'No payouts matches this search, please try with other parameters'
          : "You don't have any payouts yet"
          }}
        </p>
      </div>
      <ul>
        <li>
          <button class="btn" :disabled="!hasPrev" @click="findPrevFiatPayoutList">
            Previous
          </button>
        </li>
        <li>
          <button class="btn" :disabled="!hasNext" @click="findNextFiatPayoutList">
            Next
          </button>
        </li>
      </ul>
    </div>

    <!-- Status wiki -->
    <article class="status">
      Here is the meaning of all the statuses used in the table. You can find
      out more about them in the
      <a
        href="https://developers.triple-a.io/docs/fiat-payout-api-doc/e934c316238e0-transfer-states"
        target="_blank"
        rel="noopener">Official documentation</a>, or ask your question in the
      <a
        href="https://support.triple-a.io/knowledge"
        target="_blank"
        rel="noopener">FAQ</a>.
      <dl>
        <div>
          <dt>
            <Tag class="created">Created</Tag>
          </dt>
          <dd>Fiat payout transaction created</dd>
        </div>
        <div>
          <dt>
            <Tag class="awaiting_funds">Awaiting funds</Tag>
          </dt>
          <dd>Waiting for funds to be received to pay for the transfer</dd>
        </div>
        <div>
          <dt>
            <Tag class="confirmed">Confirmed</Tag>
          </dt>
          <dd>Confirmed by user</dd>
        </div>
        <div>
          <dt>
            <Tag class="submitted">Submitted</Tag>
          </dt>
          <dd>Submitted by system</dd>
        </div>
        <div>
          <dt>
            <Tag class="cancelled">Cancelled</Tag>
          </dt>
          <dd>Cancelled by user</dd>
        </div>
        <div>
          <dt>
            <Tag class="rejected">Rejected</Tag>
          </dt>
          <dd>Rejected by system</dd>
        </div>
        <div>
          <dt>
            <Tag class="declined">Declined</Tag>
          </dt>
          <dd>Declined by bank/operator</dd>
        </div>
        <div>
          <dt>
            <Tag class="reversed">Reversed</Tag>
          </dt>
          <dd>Cash pickup is cancelled</dd>
        </div>
        <div>
          <dt>
            <Tag class="completed">Completed</Tag>
          </dt>
          <dd>Confirmed by bank/operator or Cash is picked up</dd>
        </div>
        <div>
          <dt>
            <Tag class="available">Available</Tag>
          </dt>
          <dd>Cash is ready to pickup</dd>
        </div>
      </dl>
    </article>

    <!-- Details modal -->
    <triplea-tx-popup-fiat-payout
      :fiatPayoutDetail="fiatPayoutDetail"
      :shouldDisplay="fiatPayoutDetail !== null"
      @close-details-modal="closeDetailsModal()" />
  </main>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { mapActions, mapGetters } from 'vuex';
import TripleaPayoutsHistoryTable from '../../components/triplea-payouts-table/TripleaPayoutsTable';
import { InfoIcon } from 'vue-feather-icons';
import moment from 'moment';
import TripleaTxPopupFiatPayout from '../../components/triplea-tx-popup/TripleaTxPopupFiatPayout';

import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import fileDownload from 'js-file-download';
import Tag from '../../atoms/Tag.vue';
import FiatBalances from '../../components/balances/FiatBalances.vue';


const PER_PAGE = 100;
const defaultSearch = {
  payout_ref: '',
  sender_name: '',
  status: '',
  from_status_date: '',
  to_status_date: '',
  from_creation_date: '',
  to_creation_date: '',
  sending_currency: '',
  receiving_currency: '',
};
export default {
  components: {
    VueApexCharts,
    TripleaPayoutsHistoryTable,
    InfoIcon,
    DatePicker,
    TripleaTxPopupFiatPayout,
    Tag,
    FiatBalances,
  },
  data() {
    return {
      errorDownload: false,
      errorFind: false,
      errorForm: {
        creationDateFrom: '',
        creationDateTo: '',
      },
      shouldAdvancedSearchDisplay: false,
      searchMade: false,
      selectedPayout: null,
      fiatPayoutDetail: null,
      searchForm: defaultSearch,
    };
  },
  computed: {
    ...mapGetters([
      'cleanedTransactionsGetter',
      'fiatPayoutListGetter',
      'fiatPayoutPaginationGetter',
      'activeMerchantKeyGetter',
      'activeMerchantGetter',
      'currentActiveEntityAdminGetter',
      'supportedFiatCurrenciesGetter',
      'supportedOverallCurrenciesGetter',
      'fiatPayoutBalanceListGetter',
    ]),
    hasPrev() {
      const { page } = this.fiatPayoutPaginationGetter;
      return page > 1;
    },
    hasNext() {
      const { page, perPage, total } = this.fiatPayoutPaginationGetter;
      return page * perPage < total;
    },
  },
  watch: {
    cleanedTransactionsGetter() {
      this.initPage();
    },
    activeMerchantGetter() {
      this.initPage();
    },
  },
  mounted() {
    this.initPage();
  },
  beforeCreate() {
    this.$store.commit('UPDATE_FIAT_PAYOUTS', {});
  },
  methods: {
    ...mapActions([
      'fetchFiatPayoutsAction',
      'getMerchantPaymentRequestToken',
      'downloadFiatPayoutsCSV',
    ]),
    async initPage() {
      this.handleReset();
    },

    closeDetailsModal() {
      this.fiatPayoutDetail = null;
    },

    openDetailsModal(payout) {
      this.fiatPayoutDetail = payout;
    },

    toggleFiatAdvancedSearch() {
      this.shouldAdvancedSearchDisplay = !this.shouldAdvancedSearchDisplay;
    },

    formattedDate(date) {
      let formatted = moment(date, 'YYYY-MM-DD');

      let formattedDate = formatted.format('DD');
      let formattedMonth = formatted.format('MM');
      let formattedYear = formatted.format('YYYY');

      let newDate = `${formattedYear}-${formattedMonth}-${formattedDate}`;

      return newDate;
    },
    formatFiatPrice(value) {
      //return (value/1).toFixed(2);
      return this.numberWithCommas(value);
    },
    handleReset(event) {
      if (event) event.preventDefault();
      this.searchForm = { ...defaultSearch };
      this.findPayouts();
    },
    findPrevFiatPayoutList() {
      const { page } = this.fiatPayoutPaginationGetter;

      this.findPayouts(Number(page) - 1);
    },
    findNextFiatPayoutList() {
      const { page } = this.fiatPayoutPaginationGetter;

      this.findPayouts(Number(page) + 1);
    },

    async findPayouts(page = 1, perPage = PER_PAGE) {
      this.errorFind = false;
      this.searchMade =
        JSON.stringify(this.searchForm) !== JSON.stringify(defaultSearch);
      const merchantKey = this.activeMerchantKeyGetter;

      this.$vs.loading({
        container: this.$refs.fiatPayoutHistoryTable.$el,
        scale: 0.45,
      });

      try {
        const res = await this.getMerchantPaymentRequestToken(merchantKey);
        await this.$store.dispatch('fetchFiatPayoutsAction', {
          accessToken: res.data.access_token,
          perPage,
          page,
          ...this.standardizeQueryString(this.searchForm, true),
        });
      } catch (e) {
        this.errorFind = true;
        console.error(e);
      }

      this.$vs.loading.close(this.$refs.fiatPayoutHistoryTable.$el);
    },

    async downloadFiatPayoutsCSV() {
      const merchantKey = this.activeMerchantKeyGetter;
      this.errorDownload = false;

      this.$vs.loading({
        container: this.$refs.downloadFiatPayoutsCSVBtn.$el,
        scale: 0.45,
      });

      try {
        const res = await this.getMerchantPaymentRequestToken(merchantKey);

        const csvRes = await this.$store.dispatch('downloadFiatPayoutsCSV', {
          accessToken: res.data.access_token,
          ...this.standardizeQueryString(this.searchForm, true),
        });

        fileDownload(csvRes.data, 'fiat_payouts.csv');
      } catch (e) {
        this.errorDownload = true;
        console.error(e);
      }

      this.$vs.loading.close(this.$refs.downloadFiatPayoutsCSVBtn.$el);
    },

    handleResetform() {
      this.searchForm = defaultSearch;
      this.findPayouts();
    },
  },
};
</script>

<style lang="scss" scoped>
main.fiat-payout-history-wrapper {
  position: relative;

  &>h1 {
    text-transform: uppercase;
    font-size: 32px;
    font-weight: bold;
    color: #505050;
    margin-bottom: 24px;
  }

  form {
    margin-bottom: $margin-m;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    div {
      display: flex;
      flex-direction: row;

      fieldset {
        border: none;
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 12px;
        margin-right: 32px;
        align-items: flex-end;

        label {
          font-size: 12px;
          padding-right: 6px;
          min-width: 100px;

          &:last-of-type {
            padding-right: 0;
          }
        }

        input,
        select {
          display: block;
          width: 100%;
          height: 38px;
          border-radius: 5px;
          padding: 3px;
          border: 0.5px solid lightgrey;

          &[type='date'] {
            min-width: 150px;
          }

          &[type='email'] {
            min-width: 300px;
          }

          &.currency {
            text-transform: uppercase;
          }

          &.wallet-address {
            min-width: 300px;
          }

          &.invalid {
            border-color: red;
          }
        }

        &.dates {
          display: flex;
          align-items: flex-end;

          button {
            background-color: rgb(7, 85, 147);
            color: white;
            padding: 12px;
            margin-left: 8px;
            margin-top: 8px;
            border-radius: 6px;
            border: none;
            text-align: center;
            font-family: 'Montserrat', Helvetica, Arial, sans-serif;
            font-size: 1rem;
            max-height: 38px;

            &:hover {
              cursor: pointer;
              background-color: rgb(5, 59, 102);
            }
          }
        }

        &.references {
          p {
            label {
              span {
                font-weight: bold;
              }
            }
          }
        }
      }
    }

    ul.errors-section {
      color: red;
      font-size: 12px;
      display: block;
      width: 100%;
    }

    ul.button-grp {
      li.download {
        margin-right: 0;
        margin-left: auto;
      }

      li>a {
        padding-top: 6px;
        margin-left: 24px;
        display: flex;
        align-items: center;

        &:hover {
          text-decoration: underline;

          svg {
            fill: white;
          }
        }
      }
    }
  }

  table {
    .btn.link-style {
      background-color: transparent;
      border: none;
      color: $color-text;
      cursor: pointer;
    }
  }

  article {
    margin-top: 24px;

    dl {
      margin-top: 12px;

      div {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        margin-bottom: 12px;

        dt {
          margin-right: 6px;
        }
      }
    }
  }
}

.button-grp {
  display: flex;
  flex-wrap: wrap;
  margin-top: $margin-m;
  margin-bottom: $margin-m;
  width: 100%;

  li {
    margin-right: 8px;
    .btn {
      margin-right: 0;
    }

    &:last-of-type {
      margin-right: 0px;

      .btn {
        margin-right: 0;
      }
    }
  }
}

.email-popup {
  div {
    border: none;

    input,
    select {
      display: block;
      width: 100%;
      height: 38px;
      border-radius: 5px;
      padding: 3px;
      border: 0.5px solid lightgrey;

      &[type='email'] {
        min-width: 300px;
      }

      &.invalid {
        border-color: red;
      }
    }

    &.icon {
      p {
        position: relative;
        margin-left: 24px;

        &::before {
          content: '';
          background-image: url('../../assets/icons/checkedTask_green.svg');
          background-size: cover;
          display: inline-block;
          position: absolute;
          height: 24px;
          width: 24px;
          top: calc(50% - 12px);
          left: -30px;
        }
      }

      &.error {
        p::before {
          background-image: url('../../assets/icons/errorIcon_red.svg');
        }
      }
    }
  }

  .button-grp {
    display: flex;
    justify-content: center;
    margin-top: 20px;

    button {
      margin-right: 12px;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }
}
</style>
